import { useState, useMemo, useRef, memo, useEffect, useCallback } from 'react'
import { AgGridReact } from 'ag-grid-react'
import { ColDef } from 'ag-grid-community'
import 'ag-grid-community/styles/ag-grid.css'
import 'ag-grid-community/styles/ag-theme-material.css'
import { agConfigSideBar } from '../../../../../../utils/AgGridConfig'
import { KTSVG, toAbsoluteUrl } from '../../../../../../_metronic/helpers'
import { HStack } from '../../../../../../utils/Stack'
import {
  CompanyDataInput,
  GetCompanyWiseReportQuery,
  GetPortfolioOverviewReportQuery,
  GetUrlForS3ObjectQuery,
  useGetCompanyWiseReportQuery,
  useGetPortfolioOverviewReportQuery,
  useGetUrlForS3ObjectQuery,
} from '../../../../../../generated/graphql'
import { graphqlRequestClient } from '../../../../../../queries/client'
import { Modal } from 'react-bootstrap'
import Select from 'react-select'
import * as Yup from 'yup'
import { useFormik } from 'formik'
import { selectCustomStyles } from '../../../../../../utils/Select'
import Toast, { ToastType } from '../../../../../../utils/toast'
import CustomDownloadDialog from './CustomDialogBox'

export interface Report {
  reportName: string
  fileName: string
  description: string
  fileLink: string
}

const tableCellStyle = {
  fontSize: '14px',
  padding: '0',
  display: 'flex',
  alignItems: 'center',
  borderRight: 'none',
}

export const ReportAGTable = ({ reportData }: { reportData: Report[] }) => {
  const gridApi = useRef<any>(null)

  const gridRef = useRef<AgGridReact<Report>>(null)
  const [portfolioEnable, setPortfolioEnable] = useState(false)
  const [loading, setLoading] = useState<boolean>(false)
  const [showPopup, setShowPopup] = useState(false)

  const { data, isSuccess } = useGetPortfolioOverviewReportQuery<
    GetPortfolioOverviewReportQuery,
    Error
  >(
    graphqlRequestClient,
    {},
    {
      enabled: portfolioEnable,
      cacheTime: 0,
      staleTime: 0,
      onSuccess: (data) => {
        setPortfolioEnable(false)
      },
      onError: (error) => {
        setPortfolioEnable(false)
        Toast('Error fetching portfolio report', ToastType.error)
      },
    }
  )

  const ReportNameRenderer = useCallback((data: any) => {
    return (
      <div className='pt-2 ms-4'>
        <div className='fw-bold text-gray-900 text-hover-primary lh-sm'>{data.data.reportName}</div>
        <div className='text-muted fs-7 lh-sm'>{data.data.description}</div>
      </div>
    )
  }, [])

  const ReportsComponent = useCallback(
    (data: any) => (
      <HStack className='items-center w-fit rounded-md pb-4 mt-2'>
        <img src={toAbsoluteUrl('/media/svg/files/microsoft.svg')} alt='' height={32} width={32} />
      </HStack>
    ),
    []
  )

  const DownloadComponent = useCallback(
    (data: any) => {
      const isLoading = loading

      return (
        <p
          className={`text-blue-heading hover-primary text-sm fw-medium text-decoration-underline ms-4 cursor-pointer ${
            isLoading ? 'opacity-50 pointer-events-none' : ''
          }`}
          onClick={() => {
            if (isLoading) return
            if (data.data.reportName === 'Company Wise Report') {
              setShowPopup(true)
            } else {
              setLoading(true)
              setPortfolioEnable(true)
            }
          }}
        >
          {isLoading ? 'Downloading...' : 'Download'}
          {isLoading && (
            <span className='spinner-border spinner-border-sm align-middle ms-2'></span>
          )}
        </p>
      )
    },
    [loading]
  )

  const rowData = useMemo(() => reportData, [reportData])

  const defaultColDef = useMemo<ColDef>(
    () => ({
      sortable: true,
      autoHeight: true,
      wrapHeaderText: true,
      suppressColumnVirtualisation: true,
      columnsMenuParams: {
        suppressColumnFilter: true,
      },
      filterParams: {
        buttons: ['reset'],
        maxNumConditions: 5,
      },
      minWidth: 150,
      filter: true,
      resizable: true,
      flex: 1,
    }),
    []
  )

  const columnDefs = useMemo<ColDef<Report>[]>(
    () => [
      {
        headerName: '',
        pinned: 'left',
        width: 80,
        maxWidth: 80,
        filter: false,
        menuTabs: [],
        flex: 1,
        colId: 'action-column',
        suppressNavigable: true,
        suppressColumnsToolPanel: true,
        cellStyle: { paddingTop: '10px', lineHeight: '20px' },
        resizable: false,
        sortable: false,
        cellRenderer: ReportsComponent,
      },
      {
        headerName: 'Report Name',
        field: 'reportName',
        cellRenderer: 'reportNameRenderer',
        sortable: true,
        filter: true,
        flex: 3,
        minWidth: 400,
        cellStyle: tableCellStyle,
      },
      {
        headerName: '',
        cellRenderer: 'actionRenderer',
        sortable: false,
        filter: false,
        flex: 1,
        minWidth: 120,
        maxWidth: 150,
        cellStyle: tableCellStyle,
        suppressColumnsToolPanel: true,
        pinned: 'right',
        colId: 'action-column',
      },
    ],
    []
  )

  const componentsRegistry = useMemo(
    () => ({
      reportNameRenderer: ReportNameRenderer,
      actionRenderer: DownloadComponent,
    }),
    [ReportNameRenderer, DownloadComponent]
  )

  return (
    <div
      className='card p-0'
      style={{
        boxShadow: '0px 3px 4px rgba(0,0,0,0.03)',
        border: '1px solid #F1F1F2',
        height: '575px',
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      {showPopup && (
        <CompanySelectorPopup
          showModal={showPopup}
          handleClose={() => setShowPopup(false)}
          setKey={(link: string) => {
            // if (link) handleClick(link)
          }}
        />
      )}
      <div
        className='d-flex justify-content-between align-items-center px-9 py-6'
        style={{ borderBottom: '1px solid #F1F1F2' }}
      >
        <div className='card-title'>
          <h1 className='mb-0' style={{ fontSize: '20px', fontWeight: '600', color: '#111827' }}>
            Reports
          </h1>
        </div>
        <span
          className={`d-flex flex-column border border-1 border-secondary-txt bg-transparent h-38px items-center w-full rounded`}
          style={{
            boxShadow: '0px 3px 4px 0px rgba(0, 0, 0, 0.03)',
            border: '1',
            borderColor: '#F1F1F2',
            zIndex: 6,
          }}
        >
          <span className='d-flex flex-row'>
            <KTSVG
              path={'media/icons/duotune/general/gen021.svg'}
              className='ps-4 svg-icon-2x text-start fs-9 pt-2'
              svgClassName='text-gray-700'
            />
            <input
              type='search'
              placeholder='Search by Report Name'
              className='form-control w-auto mw-200-px border-0 h-35px rounded'
              onChange={(e: any) => {
                gridApi.current.api.setQuickFilter(e.target.value)
              }}
            />
          </span>
        </span>
      </div>
      <CustomDownloadDialog
        isOpen={loading}
        onOpenChange={(openChange: boolean) => {
          if (!openChange) {
            setLoading(false)
          }
        }}
      />

      <div className='card-body w-100 p-0 overflow-auto ag-theme-material h-100'>
        <AgGridReact
          components={componentsRegistry}
          ref={gridRef}
          onGridReady={(params) => {
            gridApi.current = params
          }}
          sideBar={agConfigSideBar}
          defaultColDef={defaultColDef}
          columnDefs={columnDefs}
          rowData={rowData}
          animateRows
          pagination={false}
          rowClass={'border-right-0 border-left-0 border-dashed'}
          overlayNoRowsTemplate={`<span style="padding:10px; border:2px solid #444; background: lightgoldenrodyellow;">No Rows To Show</span>`}
          // getRowStyle={(params) => {
          //   if (params.rowIndex % 2 === 0) {
          //     return { background: '#f8f8f8' }
          //   } else {
          //     return { background: '#ffffff' }
          //   }
          // }}
        />
      </div>
    </div>
  )
}

type Props = {
  showModal: boolean
  handleClose: () => void
  watchListName?: string
  id?: number
  setKey: Function
}
interface OptionType {
  label: string
  value: string
  type?: string
}
export const CompanySelectorPopup = (props: Props) => {
  const [loading, setLoading] = useState<boolean>(false)
  const [isEnable, setIsEnable] = useState<boolean>(false)
  const storedCompanies = sessionStorage.getItem('userCompanies')
  const userCompanies: CompanyDataInput[] = storedCompanies ? JSON.parse(storedCompanies) : []

  const companyOptions: OptionType[] = useMemo(
    () =>
      userCompanies.map((comp) => ({
        label: comp.name!,
        value: comp.type === 'HISSA' ? comp.hissaCompanyId : comp.id,
        type: comp.type,
      })),
    [userCompanies]
  )

  const initialValues = {
    selectedCompanies: [],
    isSelectAll: false,
  }

  const AddWatchlistSchema = Yup.object().shape({
    selectedCompanies: Yup.array().when('isSelectAll', {
      is: false,
      then: Yup.array()
        .min(1, 'At least one company must be selected')
        .required()
        .label('Selected Companies'),
      otherwise: Yup.array(),
    }),
    isSelectAll: Yup.boolean(),
  })

  const formik = useFormik({
    initialValues,
    validationSchema: AddWatchlistSchema,
    validateOnBlur: true,
    onSubmit: async (values) => {
      setLoading(true)
      setIsEnable(true)
    },
  })

  // Handle select all toggle
  useEffect(() => {
    if (formik.values.isSelectAll) {
      formik.setFieldValue('selectedCompanies', companyOptions)
    } else {
      formik.setFieldValue('selectedCompanies', [])
    }
  }, [formik.values.isSelectAll])

  const remainingOptions = useMemo(
    () =>
      companyOptions.filter(
        (option) =>
          !formik.values.selectedCompanies.some(
            (selected: OptionType) => selected.value === option.value
          )
      ),
    [companyOptions, formik.values.selectedCompanies]
  )

  const input: CompanyDataInput[] = useMemo(
    () =>
      formik.values.isSelectAll
        ? companyOptions.map((option) => ({
            type: option.type,
            hissaCompanyId: option.type === 'HISSA' ? option.value : undefined,
            id: option.type !== 'HISSA' ? option.value : undefined,
          }))
        : formik.values.selectedCompanies.map((option: any) => ({
            type: option.type,
            hissaCompanyId: option.type === 'HISSA' ? option.value : undefined,
            id: option.type !== 'HISSA' ? option.value : undefined,
          })),
    [formik.values.isSelectAll, formik.values.selectedCompanies, companyOptions]
  )

  useGetCompanyWiseReportQuery<GetCompanyWiseReportQuery, Error>(
    graphqlRequestClient,
    { input: input },
    {
      enabled: isEnable,
      staleTime: 0,
      cacheTime: 0,
      onSuccess: (data) => {
        if (data) {
          setIsEnable(false)
          // Don't set loading to false immediately - let the dialog handle its own timing
          // setLoading(false)
          // props.handleClose()
        }
      },
      onError: (error) => {
        setLoading(false)
        setIsEnable(false)
        Toast('Error fetching company wise report', ToastType.error)
      },
    }
  )

  return (
    <Modal
      id='kt_modal_add_inv'
      tabIndex={-1}
      aria-hidden='true'
      dialogClassName='modal-dialog modal-dialog-centered mw-500px'
      show={props.showModal}
      onHide={props.handleClose}
    >
      <div className='col d-flex justify-content-between px-9'>
        <div className='py-9'>
          <h2 className='card-header fw-bolder text-dark'>{'Company Wise Report'}</h2>
        </div>
        <button
          className='btn btn-sm btn-icon btn-active-light-primary mt-6'
          type='button'
          data-bs-toggle='tooltip'
          title='Close'
          onClick={props.handleClose}
        >
          <KTSVG className='svg-icon-1' path='/media/icons/duotune/arrows/arr061.svg' />
        </button>
      </div>
      <div className='separator'></div>

      <Modal.Body className='p-9 pt-0'>
        <CustomDownloadDialog
          isOpen={loading}
          onOpenChange={(openChange: boolean) => {
            if (!openChange) {
              setLoading(false)
              props.handleClose()
            }
          }}
        />
        <form className='card ' onSubmit={formik.handleSubmit}>
          <div className='card-body p-0'>
            <div className='row align-md-items-baseline pt-6'>
              <div className='col-6 col-md-6'>
                <label className='form-label'>Select All Companies</label>
              </div>
              <div className='col-6 col-md-5 '>
                <div className='form-check form-switch form-check-custom form-check-solid me-10'>
                  <input
                    className='form-check-input h-20px w-40px'
                    type='checkbox'
                    {...formik.getFieldProps('isSelectAll')}
                  />
                </div>
              </div>
            </div>
            {!formik.values.isSelectAll && (
              <>
                <div className='d-flex align-items-center my-3'>
                  <hr className='flex-grow-1' />
                  <span className='px-2 text-muted'>OR</span>
                  <hr className='flex-grow-1' />
                </div>

                <div className='fv-row mb-6 col-12'>
                  <label className='form-label required'>Select Companies</label>

                  <Select
                    options={remainingOptions}
                    name='company'
                    isMulti
                    placeholder='Select companies...'
                    isClearable
                    isDisabled={formik.values.isSelectAll}
                    value={formik.values.selectedCompanies}
                    onChange={(selectedOptions) =>
                      formik.setFieldValue('selectedCompanies', selectedOptions || [])
                    }
                    styles={selectCustomStyles}
                  />

                  <div className='text-danger mt-2'>
                    {formik.touched.selectedCompanies &&
                      formik.errors.selectedCompanies &&
                      !formik.values.isSelectAll && (
                        <div className='fv-plugins-message-container'>
                          <div className='fv-help-block'>{formik.errors.selectedCompanies}</div>
                        </div>
                      )}
                  </div>
                </div>
              </>
            )}

            <div className='d-flex col justify-content-between pt-4'>
              <button className='btn btn-lg btn-secondary' onClick={props.handleClose}>
                <span className='text-gray-800'>Cancel</span>
              </button>
              <button
                type='submit'
                className='btn btn-lg btn-primary'
                disabled={
                  (!formik.values.isSelectAll &&
                    (formik.errors.selectedCompanies !== undefined ||
                      formik.values.selectedCompanies.length === 0)) ||
                  loading
                }
              >
                {!loading && <span className='indicator-label'>{'Download'}</span>}
                {loading && (
                  <span className='indicator-progress' style={{ display: 'block' }}>
                    Please wait...
                    <span className='spinner-border spinner-border-sm align-middle ms-2'></span>
                  </span>
                )}
              </button>
            </div>
          </div>
        </form>
      </Modal.Body>
    </Modal>
  )
}
